import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { BUSINESS } from "@/lib/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, { ...form, language: lang });
      if (res.data?.success) {
        toast.success(t.contact.form.success);
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        toast.error(t.contact.form.error);
      }
    } catch {
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-16 md:py-20 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-10 max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-[#e52620]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 font-bold font-mono">
              {t.contact.eyebrow}
            </span>
          </div>
          <h2
            data-testid="contact-title"
            className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95]"
          >
            {t.contact.title}
          </h2>
          <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-[#0e0e0e] border border-white/10 rounded-xl p-6 md:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label={t.contact.form.name}
                name="name"
                value={form.name}
                onChange={onChange}
                required
                testid="contact-input-name"
              />
              <Field
                label={t.contact.form.email}
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                required
                testid="contact-input-email"
              />
              <Field
                label={t.contact.form.phone}
                name="phone"
                value={form.phone}
                onChange={onChange}
                testid="contact-input-phone"
              />
              <Field
                label={t.contact.form.subject}
                name="subject"
                value={form.subject}
                onChange={onChange}
                testid="contact-input-subject"
              />
            </div>
            <div className="mt-4">
              <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2 font-mono">
                {t.contact.form.message}
              </label>
              <textarea
                data-testid="contact-input-message"
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={6}
                className="w-full bg-black/40 border border-white/10 focus:border-[#e52620] outline-none rounded-md px-4 py-3 text-white placeholder-zinc-600 transition-colors resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="mt-6 inline-flex items-center gap-3 bg-[#e52620] hover:bg-[#b51e18] disabled:opacity-60 text-white px-7 py-4 rounded-md font-bold uppercase tracking-[0.12em] text-sm transition-all active:scale-95"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t.contact.form.sending}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {t.contact.form.submit}
                </>
              )}
            </button>
          </form>

          {/* Info + Map */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            <div className="bg-[#0e0e0e] border border-white/10 rounded-xl p-6">
              <InfoRow icon={MapPin} label={t.contact.info.address_label} value={`${BUSINESS.address}, ${BUSINESS.city}`} testid="info-address" />
              <InfoRow icon={Phone} label={t.contact.info.phone_label} value={`${BUSINESS.phoneOwner} · ${BUSINESS.owner}`} href={`tel:${BUSINESS.phoneOwner.replace(/\s/g, "")}`} testid="info-phone-owner" />
              <InfoRow icon={Phone} label={t.contact.info.phone_label} value={`${BUSINESS.phoneCo} · ${BUSINESS.coOwner}`} href={`tel:${BUSINESS.phoneCo.replace(/\s/g, "")}`} testid="info-phone-co" />
              <InfoRow icon={Mail} label={t.contact.info.email_label} value={BUSINESS.email} href={`mailto:${BUSINESS.email}`} testid="info-email" />
              <InfoRow icon={Clock} label={t.contact.info.hours_label} value={t.contact.info.hours} testid="info-hours" last />
            </div>

            <div className="relative rounded-xl overflow-hidden border border-white/10 aspect-[4/3]">
              <iframe
                title="MP Carrosserie Map"
                src={BUSINESS.mapsEmbed}
                className="w-full h-full grayscale contrast-125"
                style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2 font-mono">
        {label}
        {required && <span className="text-[#e52620] ml-1">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-black/40 border border-white/10 focus:border-[#e52620] outline-none rounded-md px-4 py-3 text-white placeholder-zinc-600 transition-colors"
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href, last, testid }) {
  const inner = (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-md bg-[#1a1a1a] border border-white/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-zinc-300" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold font-mono">
          {label}
        </div>
        <div className="text-white font-medium mt-0.5 break-words">
          {value}
        </div>
      </div>
    </div>
  );
  return (
    <div className={last ? "" : "pb-5 mb-5 border-b border-white/5"} data-testid={testid}>
      {href ? (
        <a href={href} className="block hover:opacity-80 transition-opacity">
          {inner}
        </a>
      ) : (
        inner
      )}
    </div>
  );
}
